const root = document.querySelector('[data-loot-redeem]');
if (!root) {
  console.warn('[loot] no [data-loot-redeem] element on page');
} else {
  console.log('[loot] booting');
}

async function boot() {
  if (!root) return;

  // Dynamic imports — if any of these fail (CDN issue, version, CSP), we
  // catch the error here and fall through to the disconnected state so
  // the user sees the connect buttons rather than a stuck spinner.
  let SessionKit, WebRenderer, WalletPluginAnchor, WalletPluginCloudWallet;
  try {
    console.log('[loot] importing wharfkit…');
    ({ SessionKit }           = await import("https://esm.sh/@wharfkit/session"));
    ({ WebRenderer }          = await import("https://esm.sh/@wharfkit/web-renderer"));
    ({ WalletPluginAnchor }   = await import("https://esm.sh/@wharfkit/wallet-plugin-anchor"));
    ({ WalletPluginCloudWallet } = await import("https://esm.sh/@wharfkit/wallet-plugin-cloudwallet"));
    console.log('[loot] wharfkit loaded');
  } catch (e) {
    console.error('[loot] failed to load WharfKit from esm.sh', e);
    root.setAttribute('data-state', 'disconnected');
    const errEl = root.querySelector('[data-loot-error-msg]');
    if (errEl) errEl.textContent = 'Wallet library failed to load: ' + (e.message || e);
    return;
  }

  const cfg = JSON.parse(root.dataset.config);
  const $ = (sel) => root.querySelector(sel);
  const setState = (s) => root.setAttribute('data-state', s);

  // ---------- WAX endpoint probe ----------
  // Greymass is preferred but goes down with some regularity; rather than
  // pin SessionKit to a dead RPC, probe a known-good fallback list in
  // parallel and pick the highest-priority survivor. The chosen URL is
  // bound to SessionKit at construction, so balance reads and transact()
  // calls both inherit it for the life of the session.
  const WAX_FALLBACKS = [
    'https://wax.greymass.com',
    'https://api.waxsweden.org',
    'https://wax.eosphere.io',
    'https://wax.eosusa.io',
    'https://api.wax.alohaeos.com',
    'https://wax.dapplica.io',
    'https://waxapi.ledgerwise.io',
  ];

  async function probeWaxEndpoint(url, timeoutMs = 4000) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch(url + '/v1/chain/get_info', {
        method: 'GET',
        signal: ctrl.signal,
        cache: 'no-store',
      });
      if (!r.ok) return { url, ok: false, reason: 'http_' + r.status };
      const j = await r.json();
      if (j && cfg.chainId && j.chain_id !== cfg.chainId) {
        return { url, ok: false, reason: 'wrong_chain' };
      }
      return { url, ok: true };
    } catch (e) {
      const reason = e && e.name === 'AbortError' ? 'timeout' : (e && e.message) || 'error';
      return { url, ok: false, reason };
    } finally {
      clearTimeout(timer);
    }
  }

  async function pickWaxEndpoint() {
    // Probe the configured URL alongside the fallbacks so a custom merchant
    // setting wins if it's alive but isn't on our hardcoded list.
    const list = [cfg.chainUrl, ...WAX_FALLBACKS.filter(u => u !== cfg.chainUrl)];
    const probes = await Promise.all(list.map(u => probeWaxEndpoint(u)));
    const alive = new Set(probes.filter(p => p.ok).map(p => p.url));
    console.log('[loot] WAX endpoint probe', probes);
    if (alive.size === 0) {
      throw new Error('No WAX RPC endpoint reachable — tried ' + list.length + '.');
    }
    return list.find(u => alive.has(u));
  }

  // ---------- formatting ----------
  const fmtToken = (n) => {
    const p = cfg.precision | 0;
    const fixed = Number(n).toFixed(p);
    const [int, dec] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return dec ? grouped + '.' + dec : grouped;
  };
  // Whole-token display — floor the fractional Loot off and group thousands.
  // Used for the headline balance where the four-decimal precision adds
  // visual noise without information.
  const fmtTokenInt = (n) => {
    const floored = Math.floor(Number(n) || 0);
    return String(floored).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const fmtAssetQty = (n) => {
    const p = cfg.precision | 0;
    return Number(n).toFixed(p);
  };
  const fmtUsd = (n) => '$' + Number(n).toFixed(2);
  // Whole-dollar variant for headline displays where cents are noise.
  const fmtUsdInt = (n) => '$' + Math.floor(Number(n) || 0).toLocaleString();
  const ceil10 = (n) => Math.ceil(Number(n) / 10) * 10;
  const lootForUsd = (usd, priceUsdPerLoot) => {
    const stepsCount = Math.round(usd / cfg.stepUsd);
    const perStep = ceil10((cfg.stepUsd / priceUsdPerLoot) * (1 + cfg.feePct / 100));
    return perStep * stepsCount;
  };
  // Inverse of lootForUsd — given a Loot balance and the current price,
  // return the largest $stepUsd-aligned USD discount the balance can buy
  // after the per-step fee inflation, capped at cfg.maxUsd.
  const maxUsdFromBalance = (balance, priceUsdPerLoot) => {
    if (!priceUsdPerLoot || priceUsdPerLoot <= 0) return 0;
    const lootPerStep = ceil10((cfg.stepUsd / priceUsdPerLoot) * (1 + cfg.feePct / 100));
    if (lootPerStep <= 0) return 0;
    const steps = Math.floor(Number(balance) / lootPerStep);
    const usd = steps * cfg.stepUsd;
    const cap = Number(cfg.maxUsd) || Infinity;
    return Math.max(0, Math.min(usd, cap));
  };

  // ---------- session kit ----------
  // Built lazily inside restoreOrShow() once probeWaxEndpoint() has chosen
  // a reachable URL — pinning at module load would lock us to a possibly-
  // dead greymass before we've checked.
  let sessionKit = null;
  let chosenWaxUrl = null;

  let session = null;
  let lootPriceUsd = 0;
  let balanceLoot = 0;
  let qty = 1;
  let issued = null;
  // Receipt email — resolved only AFTER WAX auth (see loadReceiptEmail).
  // receiptLocked means an address was already on file in Supabase and the
  // user can't change it; otherwise the input is editable (and may be blank).
  let receiptEmail = '';
  let receiptLocked = false;

  // ---------- price ----------
  function extractPrice(payload) {
    const r = Array.isArray(payload) ? payload[0] : payload;
    if (!r || typeof r !== 'object') return NaN;
    const source = (cfg.priceSource || 'latest').toLowerCase();
    if (source === 'vwap_14d' && r.average_14d) {
      return Number(r.average_14d.vwap_usd ?? r.average_14d.simple_usd);
    }
    if (source === 'simple_14d' && r.average_14d) {
      return Number(r.average_14d.simple_usd ?? r.average_14d.vwap_usd);
    }
    if (r.latest) return Number(r.latest.usd);
    return Number(r.usd ?? r.price ?? r.price_usd ?? r.loot_usd);
  }

  // Hard floor that protects the user from runaway Loot costs if the
  // market price feed dips into pico-cent territory. Never displayed —
  // only fed into the cost math. Mirrored server-side in the
  // "API - Loot Redeem Intent" workflow; keep both in lockstep.
  const LOOT_PRICE_FLOOR_USD = 0.00001;

  async function fetchPrice() {
    const r = await fetch(cfg.priceUrl, { cache: 'no-store' });
    if (!r.ok) throw new Error('price ' + r.status);
    const j = await r.json();
    const p = extractPrice(j);
    if (!Number.isFinite(p) || p <= 0) throw new Error('bad price');
    // Apply floor — actual market price is kept off the page entirely,
    // we only ever use the floored value in calculations.
    lootPriceUsd = Math.max(p, LOOT_PRICE_FLOOR_USD);
  }

  // ---------- balance ----------
  async function fetchBalance() {
    if (!session) return 0;
    try {
      const result = await session.client.v1.chain.get_currency_balance(
        cfg.contract,
        String(session.actor),
        cfg.symbol,
      );
      const asset = Array.isArray(result) ? result[0] : result;
      if (!asset) { balanceLoot = 0; return 0; }
      balanceLoot = Number(String(asset).split(' ')[0]);
      return balanceLoot;
    } catch (e) {
      console.warn('[loot] balance fetch failed', e);
      balanceLoot = 0;
      return 0;
    }
  }

  // ---------- UI ----------
  function renderConnected() {
    $('[data-loot-account]').textContent = String(session.actor);
    $('[data-loot-balance]').textContent = fmtTokenInt(balanceLoot);
    // Show the max redeemable discount (fee-adjusted) rather than the raw
    // USD value of the balance — that's the number the user can actually
    // act on, and it matches the copy "Up to $X USD available toward
    // shop discount."
    $('[data-loot-balance-usd]').textContent = fmtUsdInt(maxUsdFromBalance(balanceLoot, lootPriceUsd));
    renderStepper();
  }

  function renderStepper() {
    const usd = qty * cfg.stepUsd;
    const cost = lootForUsd(usd, lootPriceUsd);
    const maxUsd = Number(cfg.maxUsd) || Infinity;
    const atCap = usd >= maxUsd;

    $('[data-loot-usd]').textContent = '$' + usd;
    // Cost is always ceil10()'d so the fractional component is zero —
    // render as a grouped integer to match the "Sending X Loot" copy.
    $('[data-loot-cost]').textContent = fmtTokenInt(cost);

    const canAfford = cost <= balanceLoot && lootPriceUsd > 0;
    const submit = $('[data-loot-submit]');
    submit.disabled = !canAfford;
    $('[data-loot-submit-text]').textContent = canAfford
      ? 'Get $' + usd + ' USD Discount'
      : 'Insufficient Loot';

    // Disable + when at cap or next step is unaffordable
    const incBtn = $('[data-loot-inc]');
    if (incBtn) {
      const nextCost = lootForUsd((qty + 1) * cfg.stepUsd, lootPriceUsd);
      const nextWouldExceedCap = (qty + 1) * cfg.stepUsd > maxUsd;
      incBtn.disabled = nextWouldExceedCap || nextCost > balanceLoot;
    }
    // Disable − at the minimum step so the user can't try to go below it.
    const decBtn = $('[data-loot-dec]');
    if (decBtn) decBtn.disabled = qty <= 1;

    // Show / hide the per-transaction cap hint
    const capHint = $('[data-loot-cap-hint]');
    if (capHint) capHint.hidden = !atCap;

    const errEl = $('[data-loot-error]');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
  }

  function bumpQty(delta) {
    const next = qty + delta;
    if (next < 1) return;
    const nextUsd = next * cfg.stepUsd;
    const maxUsd = Number(cfg.maxUsd) || Infinity;
    if (delta > 0 && nextUsd > maxUsd) return; // hard cap
    const cost = lootForUsd(nextUsd, lootPriceUsd);
    if (delta > 0 && cost > balanceLoot) return;
    qty = next;
    renderStepper();
  }

  // Mirror the actor into a stable localStorage key so other parts of the
  // theme (e.g. the header wallet indicator, digital-PDP wallet input) can
  // prefill without coupling to WharfKit's internal storage key shape.
  // Storage events only fire cross-tab; dispatch a CustomEvent so same-tab
  // listeners (header) update without polling.
  function mirrorActor(actor) {
    try {
      if (actor) localStorage.setItem('warsaken-wax-account', String(actor));
      else localStorage.removeItem('warsaken-wax-account');
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('warsaken:wax-account', {
        detail: { actor: actor ? String(actor) : null }
      }));
    } catch (_) {}
  }

  // ---------- receipt email ----------
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const RECEIPT_KEY = 'warsaken-receipt-email';

  function setReceiptState(state) {
    const el = $('[data-loot-receipt]');
    if (el) el.setAttribute('data-receipt-state', state);
  }

  function clearReceiptError() {
    const input = $('[data-loot-email-input]');
    if (input) input.classList.remove('is-invalid');
    const errEl = $('[data-loot-error]');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
  }

  function prefillReceiptInput() {
    const input = $('[data-loot-email-input]');
    if (!input) return;
    try {
      const saved = localStorage.getItem(RECEIPT_KEY);
      if (saved && !input.value) input.value = saved;
    } catch (_) {}
  }

  // Ask the backend whether this WAX actor already has a receipt email on file.
  // Called ONLY from the connected paths — after sessionKit.login()/restore()
  // has resolved — so the check is never offered before auth finishes. Outcomes:
  //   on file → lock it (read-only; the user can't change it)
  //   none    → show the editable input (they may type one or leave it blank)
  //   error   → fall back to the editable input so a redemption isn't blocked
  async function loadReceiptEmail() {
    receiptEmail = '';
    receiptLocked = false;
    const input = $('[data-loot-email-input]');
    if (input) { input.value = ''; input.classList.remove('is-invalid'); }

    if (!session) { setReceiptState('hidden'); return; }
    if (!cfg.emailLookupUrl) { setReceiptState('input'); prefillReceiptInput(); return; }

    setReceiptState('checking');
    try {
      const r = await fetch(cfg.emailLookupUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actor: String(session.actor) }),
        cache: 'no-store',
      });
      const raw = await r.text();
      let parsed;
      try { parsed = JSON.parse(raw); } catch (_) { parsed = raw; }
      const j = Array.isArray(parsed) ? parsed[0] : parsed;
      const onFile = (j && typeof j === 'object') ? (j.email || '') : '';
      if (r.ok && onFile && EMAIL_RE.test(String(onFile).trim())) {
        receiptEmail = String(onFile).trim();
        receiptLocked = true;
        const emailEl = $('[data-loot-receipt-email]');
        if (emailEl) emailEl.textContent = receiptEmail;
        setReceiptState('locked');
        return;
      }
    } catch (e) {
      console.warn('[loot] email lookup failed', e);
    }
    // none on file (or the lookup failed) → let them enter one
    setReceiptState('input');
    prefillReceiptInput();
  }

  // The email to send with the redeem call. '' = skip (allowed); null = the
  // user typed something malformed (caller blocks and surfaces the error).
  function readReceiptEmail() {
    if (receiptLocked) return receiptEmail;
    const input = $('[data-loot-email-input]');
    const val = input ? input.value.trim() : '';
    if (!val) return '';
    if (!EMAIL_RE.test(val)) return null;
    return val;
  }

  // ---------- flow ----------
  async function ensureSessionKit() {
    if (sessionKit) return sessionKit;
    chosenWaxUrl = await pickWaxEndpoint();
    console.log('[loot] using WAX endpoint', chosenWaxUrl);
    sessionKit = new SessionKit({
      appName: cfg.appName,
      chains: [{ id: cfg.chainId, url: chosenWaxUrl }],
      ui: new WebRenderer(),
      walletPlugins: [new WalletPluginAnchor(), new WalletPluginCloudWallet()],
    });
    return sessionKit;
  }

  async function restoreOrShow() {
    setState('loading');
    try { await fetchPrice(); } catch (e) { console.warn('[loot] price', e); }
    try {
      await ensureSessionKit();
    } catch (e) {
      console.error('[loot] no WAX endpoint reachable', e);
      showError('WAX RPC is unreachable right now. Please try again in a few minutes.');
      return;
    }
    try {
      const r = await sessionKit.restore();
      if (r) {
        session = r;
        mirrorActor(session.actor);
        await fetchBalance();
        renderConnected();
        setState('connected');
        // Auth has resolved — now (and only now) offer the email check.
        // Fire-and-forget so wallet info isn't held back by the lookup.
        loadReceiptEmail();
        return;
      }
    } catch (e) { console.warn('[loot] restore failed', e); }
    setState('disconnected');
  }

  async function login(walletId) {
    setState('loading');
    try {
      if (!lootPriceUsd) await fetchPrice();
      await ensureSessionKit();
      const { session: s } = await sessionKit.login({ walletPlugin: { id: walletId } });
      session = s;
      mirrorActor(session.actor);
      await fetchBalance();
      qty = 1;
      renderConnected();
      setState('connected');
      // login() only resolves once the wallet finishes authenticating, so
      // this is the safe point to look up an email on file. Non-blocking.
      loadReceiptEmail();
    } catch (e) {
      console.warn('[loot] login failed', e);
      setState('disconnected');
    }
  }

  async function logout() {
    try { await sessionKit.logout(session); } catch (_) {}
    mirrorActor(null);
    session = null;
    balanceLoot = 0;
    qty = 1;
    receiptEmail = '';
    receiptLocked = false;
    setReceiptState('hidden');
    setState('disconnected');
  }

  async function submit() {
    if (!session || lootPriceUsd <= 0) return;
    const usd = qty * cfg.stepUsd;
    const cost = lootForUsd(usd, lootPriceUsd);
    if (cost > balanceLoot) return;

    // Snapshot the receipt email. Blank is fine (no email sent); a malformed
    // entry blocks here with an inline message rather than going on-chain.
    const receipt = readReceiptEmail();
    if (receipt === null) {
      const input = $('[data-loot-email-input]');
      if (input) input.classList.add('is-invalid');
      const errEl = $('[data-loot-error]');
      if (errEl) { errEl.hidden = false; errEl.textContent = 'Enter a valid email, or clear the field to skip the receipt.'; }
      return;
    }
    if (receipt && !receiptLocked) {
      try { localStorage.setItem(RECEIPT_KEY, receipt); } catch (_) {}
    }

    let intent;
    try {
      const r = await fetch(cfg.intentUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actor: String(session.actor),
          usd_amount: usd,
          quoted_loot: cost,
          loot_price_usd: lootPriceUsd,
        }),
      });
      const raw = await r.text();
      let parsed;
      try { parsed = JSON.parse(raw); } catch (_) { parsed = raw; }
      console.log('[loot] intent response', r.status, parsed);
      if (!r.ok) throw new Error('intent ' + r.status + ' ' + (typeof parsed === 'string' ? parsed : JSON.stringify(parsed)));
      intent = Array.isArray(parsed) ? parsed[0] : parsed;
      if (!intent || !intent.nonce) throw new Error('intent payload missing nonce: ' + raw.slice(0, 200));
    } catch (e) {
      console.warn('[loot] intent failed', e);
      showError("Couldn't prepare your redemption: " + (e.message || e));
      return;
    }

    $('[data-loot-sign-cost]').textContent = fmtToken(intent.loot_cost ?? cost);
    $('[data-loot-sign-usd]').textContent = String(intent.usd_amount ?? usd);
    setState('signing');

    const finalCost = intent.loot_cost ?? cost;
    const memo = 'WARSAKEN® LOOT REDEMPTION - ' + intent.nonce;
    const quantity = fmtAssetQty(finalCost) + ' ' + cfg.symbol;

    let txResult;
    try {
      txResult = await session.transact({
        actions: [{
          account: cfg.contract,
          name: 'transfer',
          authorization: [session.permissionLevel],
          data: {
            from: String(session.actor),
            to: cfg.sendTo,
            quantity,
            memo,
          },
        }],
      });
    } catch (e) {
      console.warn('[loot] sign failed', e);
      setState('connected');
      showError('Transaction was cancelled or failed in your wallet.');
      return;
    }

    setState('issuing');
    let tx_id = '';
    try {
      tx_id = String(txResult.response?.transaction_id ?? txResult.resolved?.transaction.id ?? '');
    } catch (_) {}
    console.log('[loot] signed tx_id', tx_id);

    try {
      const r = await fetch(cfg.redeemUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nonce: intent.nonce,
          tx_id,
          actor: String(session.actor),
          usd_amount: intent.usd_amount ?? usd,
          loot_cost: finalCost,
          email: receipt, // '' when skipped; backend sends a receipt only when set
        }),
      });
      const raw = await r.text();
      let parsed;
      try { parsed = JSON.parse(raw); } catch (_) { parsed = raw; }
      console.log('[loot] redeem response', r.status, parsed);
      if (!r.ok) throw new Error('redeem ' + r.status + ' ' + (typeof parsed === 'string' ? parsed : JSON.stringify(parsed)));
      const j = Array.isArray(parsed) ? parsed[0] : parsed;
      if (!j || !j.code) throw new Error('redeem payload missing code: ' + raw.slice(0, 200));
      issued = j;
      renderRedeemed();
      setState('redeemed');
      fetchBalance().then(() => {
        if (root.getAttribute('data-state') === 'connected') renderConnected();
      });
    } catch (e) {
      console.warn('[loot] redeem failed', e, 'tx_id:', tx_id);
      showError(
        "Your Loot was sent (tx " + (tx_id || 'pending') + "), but the discount code didn't come back. " +
        "Email support with that transaction ID and we'll mint your code by hand. " +
        "Detail: " + (e.message || e)
      );
    }
  }

  function showError(msg) {
    const errEl = $('[data-loot-error-msg]');
    if (errEl) errEl.textContent = msg;
    setState('error');
  }

  function renderRedeemed() {
    if (!issued) return;
    $('[data-loot-code]').textContent = issued.code;
    $('[data-loot-code-usd]').textContent = String(issued.usd_amount ?? qty * cfg.stepUsd);
    const exp = issued.expires_at ? new Date(issued.expires_at) : null;
    $('[data-loot-code-expires]').textContent = exp
      ? exp.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : (cfg.expireDays + ' days');
    // Reset apply button to a fresh state for this code.
    const apply = $('[data-loot-apply]');
    apply.disabled = false;
    apply.classList.remove('btn--applied');
    const applyLabel = apply.querySelector('[data-loot-apply-label]') || apply;
    applyLabel.textContent = 'Apply now';
    // Auto-apply the moment the code lands. The button stays visible
    // as the visual record + a manual retry surface if auto-apply
    // fails silently — see the auto:true branch in applyToCart.
    applyToCart(apply, { auto: true });
  }

  async function applyToCart(btn, { auto = false } = {}) {
    if (!issued) return;
    btn.disabled = true;
    const label = btn.querySelector('[data-loot-apply-label]') || btn;
    label.textContent = 'Applying…';
    try {
      // Same-origin GET to /discount/<code> sets the discount cookie. We use
      // redirect: 'manual' so the browser honors the Set-Cookie from the 302
      // without following the redirect — keeps the user on the Loot page.
      await fetch('/discount/' + encodeURIComponent(issued.code), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
      label.textContent = 'Applied';
      btn.classList.add('btn--applied');
      // Tell the cart drawer (and anything else listening) that cart
      // state may have changed — the discount cookie was just set, so
      // /cart.js will now reflect the discount and line totals.
      try {
        window.dispatchEvent(new CustomEvent('warsaken:cart-refresh'));
      } catch (_) {}
    } catch (e) {
      console.warn('[loot] apply failed', e);
      if (auto) {
        // Auto-apply on render — don't yank the user off the page on
        // failure. Reset the button so they can click to retry.
        label.textContent = 'Apply now';
        btn.disabled = false;
      } else {
        // Manual click — fall back to a full navigation so they still
        // get the discount applied somehow.
        window.location.href = '/discount/' + encodeURIComponent(issued.code) + '?redirect=/cart';
      }
    }
  }

  // Stepper press-and-hold — first tick on pointerdown (immediate visual
  // feedback), then after a short pause an interval kicks in for rapid
  // repeats. Released or pointer-cancelled stops the run. We bind this
  // outside the click delegate so the click handler doesn't double-fire
  // a bump after a hold release.
  function attachStepperHold(btn, delta) {
    if (!btn) return;
    let holdTimer = null;
    let repeatInterval = null;
    const stop = () => {
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
      if (repeatInterval) { clearInterval(repeatInterval); repeatInterval = null; }
    };
    const tick = () => {
      if (btn.disabled) { stop(); return; }
      bumpQty(delta);
    };
    btn.addEventListener('pointerdown', (e) => {
      if (btn.disabled) return;
      e.preventDefault();
      bumpQty(delta);
      // 400ms pause before the repeat run starts — long enough that a
      // single tap doesn't feel jumpy, short enough that a deliberate
      // hold catches the next tick fast.
      holdTimer = setTimeout(() => {
        repeatInterval = setInterval(tick, 80);
      }, 400);
    });
    ['pointerup', 'pointerleave', 'pointercancel'].forEach(ev => {
      btn.addEventListener(ev, stop);
    });
    // Safety: if the window loses focus mid-hold, stop the run so we
    // don't keep incrementing while the user is in another tab.
    window.addEventListener('blur', stop);
  }
  attachStepperHold($('[data-loot-inc]'), +1);
  attachStepperHold($('[data-loot-dec]'), -1);

  // Clear the malformed-email styling the moment the user edits the field.
  const emailInput = $('[data-loot-email-input]');
  if (emailInput) emailInput.addEventListener('input', clearReceiptError);

  root.addEventListener('click', (e) => {
    const t = e.target.closest('[data-loot-connect],[data-loot-disconnect],[data-loot-submit],[data-loot-copy],[data-loot-again],[data-loot-retry],[data-loot-apply]');
    if (!t) return;
    if (t.matches('[data-loot-connect]'))    return login(t.getAttribute('data-loot-connect'));
    if (t.matches('[data-loot-disconnect]')) return logout();
    if (t.matches('[data-loot-submit]'))     return submit();
    if (t.matches('[data-loot-apply]'))      return applyToCart(t);
    if (t.matches('[data-loot-again]')) {
      issued = null;
      qty = 1;
      Promise.all([fetchPrice().catch(() => {}), fetchBalance()]).then(() => {
        renderConnected();
        setState('connected');
      });
      return;
    }
    if (t.matches('[data-loot-retry]')) {
      // If we got here from an endpoint-probe failure, sessionKit is still
      // null — re-run the whole boot flow so we re-probe and try again.
      if (!sessionKit) {
        restoreOrShow();
        return;
      }
      setState(session ? 'connected' : 'disconnected');
      return;
    }
    if (t.matches('[data-loot-copy]') && issued) {
      navigator.clipboard?.writeText(issued.code);
      // Only flip the label span — the icon next to it stays put.
      const label = t.querySelector('[data-loot-copy-label]') || t;
      label.textContent = 'Copied';
      setTimeout(() => { label.textContent = 'Copy'; }, 1500);
      return;
    }
  });

  restoreOrShow();
}

boot().catch((e) => {
  console.error('[loot] boot crashed', e);
  if (root) {
    root.setAttribute('data-state', 'error');
    const errEl = root.querySelector('[data-loot-error-msg]');
    if (errEl) errEl.textContent = 'Initialization failed: ' + (e.message || e);
  }
});
